import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";
import { addFolderShare, getFolderShares, removeFolderShare } from "../../api/workspaceApi";
import "./workspace.css";

/**
 * Shares one of my folders with a colleague.
 *
 * <b>There is no directory to pick from, by design.</b> The organization's staff list is admin-gated so
 * an ordinary user cannot enumerate everyone's names and e-mail addresses, and this is not an admin
 * screen — so you type the colleague you already know and the server confirms the name is real.
 *
 * A share carries visibility of the FILING, not of the document: the grantee still opens each document
 * through the ordinary access checks, so this cannot hand anyone a document they were not already
 * entitled to read.
 */
export default function WorkspaceShareModal({ open, folder, onClose, onChanged }) {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [shares, setShares] = useState([]);
  const [who, setWho] = useState("");
  const [canWrite, setCanWrite] = useState(false);
  const [includeSubtree, setIncludeSubtree] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const reload = useCallback(async () => {
    if (!folder) return;
    try {
      const list = await getFolderShares(token, folder.id);
      setShares(Array.isArray(list) ? list : []);
    } catch {
      setShares([]);
    }
  }, [folder, token]);

  useEffect(() => {
    if (!open) return;
    setWho("");
    setCanWrite(false);
    setIncludeSubtree(true);
    setError("");
    reload();
  }, [open, reload]);

  const add = useCallback(async () => {
    const typed = who.trim();
    if (!typed) return;
    setBusy(true);
    setError("");
    try {
      await addFolderShare(token, folder.id, { granteeUserName: typed, canWrite, includeSubtree });
      setWho("");
      await reload();
      onChanged && onChanged();
    } catch (e) {
      setError(e?.message || t("workspace.shareFailed", { defaultValue: "The folder could not be shared." }));
    } finally {
      setBusy(false);
    }
  }, [who, canWrite, includeSubtree, folder, token, reload, onChanged, t]);

  const revoke = useCallback(async (shareId) => {
    setBusy(true);
    try {
      await removeFolderShare(token, shareId);
      await reload();
      onChanged && onChanged();
    } catch (e) {
      setError(e?.message || t("workspace.revokeFailed", { defaultValue: "The share could not be removed." }));
    } finally {
      setBusy(false);
    }
  }, [token, reload, onChanged, t]);

  if (!open) return null;

  return (
    <div className="dbModalOverlay" onClick={busy ? undefined : onClose}>
      <div className="dbModalCard ws-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="dbModalTitle">
          {t("workspace.shareTitle", { defaultValue: "Share this folder" })}
        </div>
        <div className="ws-field__hint" style={{ marginBottom: 16 }}>
          {folder?.path || folder?.name}
        </div>

        {error && <div className="ws-error">{error}</div>}

        {shares.length > 0 && (
          <div className="ws-share-list">
            {shares.map((s) => (
              <div key={s.id} className="ws-share-row">
                <span className="ws-share-row__who">{s.granteeUserName}</span>
                <span className="ws-share-row__mode">
                  {s.canWrite
                    ? t("workspace.canEdit", { defaultValue: "Can add & remove" })
                    : t("workspace.canView", { defaultValue: "Can view" })}
                  {s.includeSubtree
                    ? ` · ${t("workspace.withSubfolders", { defaultValue: "with subfolders" })}`
                    : ""}
                </span>
                <button
                  type="button"
                  className="ws-doc__remove"
                  title={t("workspace.revoke", { defaultValue: "Stop sharing" })}
                  onClick={() => revoke(s.id)}
                  disabled={busy}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="ws-field">
          <label className="ws-field__label">
            {t("workspace.shareWith", { defaultValue: "Share with" })}
          </label>
          <input
            className="ws-field__input"
            value={who}
            onChange={(e) => setWho(e.target.value)}
            placeholder={t("workspace.shareWithPlaceholder", { defaultValue: "User name or e-mail address" })}
            onKeyDown={(e) => { if (e.key === "Enter") add(); }}
          />
          <span className="ws-field__hint">
            {t("workspace.shareWithHint", {
              defaultValue:
                "Sharing shows a colleague what is filed here. They still need their own access to open each document.",
            })}
          </span>
        </div>

        <label className="ws-check">
          <input type="checkbox" checked={canWrite} onChange={(e) => setCanWrite(e.target.checked)} />
          {t("workspace.allowEditing", { defaultValue: "Let them add and remove documents" })}
        </label>

        <label className="ws-check">
          <input type="checkbox" checked={includeSubtree} onChange={(e) => setIncludeSubtree(e.target.checked)} />
          {t("workspace.includeSubfolders", { defaultValue: "Include subfolders" })}
        </label>

        <div className="ws-modal-actions">
          <button type="button" className="ws-btn" onClick={onClose} disabled={busy}>
            {t("common.close", { defaultValue: "Close" })}
          </button>
          <button
            type="button"
            className="ws-btn ws-btn--primary"
            onClick={add}
            disabled={busy || !who.trim()}
          >
            {t("workspace.share", { defaultValue: "Share" })}
          </button>
        </div>
      </div>
    </div>
  );
}
